import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { Application, ApplicationQueueStats, ApplicationStatus } from '../../types';
import { ToastContainer } from '../../components/Toast';
import { useToast } from '../../hooks/useToast';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorBanner } from '../../components/ErrorBanner';
import { useErrorBanner } from '../../hooks/useErrorBanner';
import {
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

const STATUS_LABELS: Record<ApplicationStatus, string> = {
  pending_approval: 'Pending Approval',
  pending_payment: 'Pending Payment',
  approved: 'Approved',
  payment_received: 'Payment Received',
  rejected: 'Rejected',
  converted: 'Converted',
  expired: 'Expired',
};

const STATUS_COLORS: Record<ApplicationStatus, string> = {
  pending_approval: 'bg-yellow-100 text-yellow-800',
  pending_payment: 'bg-orange-100 text-orange-800',
  approved: 'bg-green-100 text-green-800',
  payment_received: 'bg-blue-100 text-blue-800',
  rejected: 'bg-red-100 text-red-800',
  converted: 'bg-purple-100 text-purple-800',
  expired: 'bg-gray-100 text-gray-800',
};

export const ApplicationQueue: React.FC = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState<Application[]>([]);
  const [stats, setStats] = useState<ApplicationQueueStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [formTypeFilter, setFormTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Quick action state
  const [confirmAction, setConfirmAction] = useState<{
    type: 'approve' | 'reject';
    app: Application;
  } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const { toasts, removeToast, showSuccess, showError } = useToast();
  const { error, showError: showBannerError, clearError } = useErrorBanner();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [appsResult, statsResult] = await Promise.all([
        api.getApplications({
          status: statusFilter || undefined,
          formType: formTypeFilter || undefined,
          search: search || undefined,
          page: pagination.page,
          limit: pagination.limit,
        }),
        api.getApplicationStats(),
      ]);
      setApplications(appsResult.applications);
      setPagination((prev) => ({ ...prev, ...appsResult.pagination }));
      setStats(statsResult);
    } catch (err) {
      showBannerError(err instanceof Error ? err.message : 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, formTypeFilter, search, pagination.page, pagination.limit, showBannerError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleQuickAction = async () => {
    if (!confirmAction) return;
    setActionLoading(true);
    try {
      const newStatus = confirmAction.type === 'approve' ? 'approved' : 'rejected';
      await api.updateApplicationStatus(confirmAction.app.id, { status: newStatus });
      showSuccess(
        `Application ${confirmAction.type === 'approve' ? 'Approved' : 'Rejected'}`,
        `${confirmAction.app.organisationName} has been ${newStatus}.`
      );
      setConfirmAction(null);
      fetchData();
    } catch (err) {
      showError(
        'Action Failed',
        err instanceof Error ? err.message : 'Failed to update application status'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const canApprove = (app: Application) => app.status === 'pending_approval';
  const canReject = (app: Application) =>
    app.status === 'pending_approval' || app.status === 'approved';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-base-100 shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-base-content mb-2">Application Queue</h1>
        <p className="text-base-content/70">Manage signup applications for Free and Flex tiers</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {(
            [
              [
                'pendingApproval',
                'Pending Approval',
                'bg-yellow-50 text-yellow-700 border-yellow-200',
              ],
              [
                'pendingPayment',
                'Pending Payment',
                'bg-orange-50 text-orange-700 border-orange-200',
              ],
              ['approved', 'Approved', 'bg-green-50 text-green-700 border-green-200'],
              ['paymentReceived', 'Payment Received', 'bg-blue-50 text-blue-700 border-blue-200'],
              ['converted', 'Converted', 'bg-purple-50 text-purple-700 border-purple-200'],
              ['rejected', 'Rejected', 'bg-red-50 text-red-700 border-red-200'],
              ['expired', 'Expired', 'bg-gray-50 text-gray-700 border-gray-200'],
            ] as const
          ).map(([key, label, colors]) => (
            <div key={key} className={`rounded-lg border p-3 ${colors}`}>
              <div className="text-2xl font-bold">{stats[key]}</div>
              <div className="text-xs font-medium">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-base-100 shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label
              htmlFor="statusFilter"
              className="block text-sm font-medium text-base-content mb-1"
            >
              Status
            </label>
            <select
              id="statusFilter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Statuses</option>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="formTypeFilter"
              className="block text-sm font-medium text-base-content mb-1"
            >
              Form Type
            </label>
            <select
              id="formTypeFilter"
              value={formTypeFilter}
              onChange={(e) => {
                setFormTypeFilter(e.target.value);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Types</option>
              <option value="free">Free</option>
              <option value="flexible-pricing">Flexible Pricing</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label htmlFor="search" className="block text-sm font-medium text-base-content mb-1">
              Search
            </label>
            <form onSubmit={handleSearch} className="flex">
              <input
                id="search"
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by email, org name, or name..."
                className="flex-1 px-3 py-2 border border-base-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                type="submit"
                className="px-4 py-2 bg-primary text-white rounded-r-md hover:bg-primary/80"
              >
                <MagnifyingGlassIcon className="h-5 w-5" />
              </button>
            </form>
          </div>
        </div>
      </div>

      <ErrorBanner error={error} onClose={clearError} />

      {/* Table */}
      <div className="bg-base-100 shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : applications.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-base-content/70 text-lg">No applications found</p>
            <p className="text-base-content/50 text-sm mt-1">
              Try adjusting your filters or search criteria
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-base-300">
            <thead className="bg-base-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Organisation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Submitted
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-base-100 divide-y divide-base-300">
              {applications.map((app) => (
                <tr
                  key={app.id}
                  className="hover:bg-base-200 cursor-pointer"
                  onClick={() => navigate(`/signup-queue/${app.id}`)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-base-content">
                      {app.organisationName}
                    </div>
                    {app.firstName && (
                      <div className="text-sm text-base-content/70">
                        {app.firstName} {app.lastName}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-base-content">
                    {app.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        app.formType === 'free'
                          ? 'bg-gray-100 text-gray-700'
                          : 'bg-indigo-100 text-indigo-700'
                      }`}
                    >
                      {app.formType === 'free' ? 'Free' : 'Flex'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${STATUS_COLORS[app.status]}`}
                    >
                      {STATUS_LABELS[app.status]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-base-content/70">
                    {new Date(app.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div
                      className="flex justify-end space-x-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {canApprove(app) && (
                        <button
                          onClick={() => setConfirmAction({ type: 'approve', app })}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200"
                          title="Approve"
                        >
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          Approve
                        </button>
                      )}
                      {canReject(app) && (
                        <button
                          onClick={() => setConfirmAction({ type: 'reject', app })}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200"
                          title="Reject"
                        >
                          <XCircleIcon className="h-4 w-4 mr-1" />
                          Reject
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-base-100 px-4 py-3 flex items-center justify-between border-t border-base-300">
            <div className="text-sm text-base-content">
              Showing page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page <= 1}
                className="px-3 py-1 border border-base-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-base-200"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= pagination.totalPages}
                className="px-3 py-1 border border-base-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-base-200"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Quick Action Confirm Dialog */}
      <ConfirmDialog
        isOpen={!!confirmAction}
        title={confirmAction?.type === 'approve' ? 'Approve Application' : 'Reject Application'}
        message={
          confirmAction
            ? `Are you sure you want to ${confirmAction.type} the application from "${confirmAction.app.organisationName}" (${confirmAction.app.email})?`
            : ''
        }
        confirmLabel={
          actionLoading ? 'Processing...' : confirmAction?.type === 'approve' ? 'Approve' : 'Reject'
        }
        cancelLabel="Cancel"
        onConfirm={handleQuickAction}
        onCancel={() => setConfirmAction(null)}
        confirmDisabled={actionLoading}
      />

      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};

export default ApplicationQueue;
